import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@headlessui/react';
import { 
  FaPlus, FaFolder, FaEdit, FaTrash, FaEye, FaMobile, FaGlobe, FaDesktop, FaQuestionCircle,
  FaChevronLeft, FaChevronRight, FaCheck
} from 'react-icons/fa';
import { 
  getToken, 
  getUser, 
  isTokenValid, 
  redirectToLogin,
  authenticatedFetch,
  getBaseUrl
} from '../utils/tokenUtils';
import ProjectCard from './ProjectCard';
import ProjectForm from './ProjectForm';
import UpgradeModal from './UpgradeModal';
import VideoModal from './VideoModal';
import { getApiUrl } from '../utils/tokenUtils';

const ProjectRow = ({ project, onView, onEdit, getBaseUrl, getStatusDotColor }) => {
  const [featureCounts, setFeatureCounts] = useState({
    products: 0,
    landingPages: 0,
    supportModules: 0,
    shortLinks: 0,
    documents: 0,
    internalAds: 0
  });

  useEffect(() => {
    const fetchFeatureCounts = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const headers = { 'Authorization': `Bearer ${token}` };

        const [landingPagesRes, supportModulesRes, shortLinksRes, documentsRes, internalAdsRes] = await Promise.allSettled([
          fetch(getApiUrl(`/api/landing-pages?projectId=${project._id}`), { headers }),
          fetch(getApiUrl(`/api/support/user/support-modules?projectId=${project._id}`), { headers }),
          fetch(getApiUrl(`/api/shortlink?projectId=${project._id}`), { headers }),
          fetch(getApiUrl(`/api/dashboard/user/projects/${project._id}/has-documents`), { headers }),
          fetch(getApiUrl(`/api/internal-ads/placements?projectId=${project._id}`), { headers })
        ]);

        const newCounts = {
          products: project.metadata?.totalProducts || 0,
          landingPages: 0,
          supportModules: 0,
          shortLinks: 0,
          documents: 0,
          internalAds: 0
        };

        if (landingPagesRes.status === 'fulfilled' && landingPagesRes.value.ok) {
          const data = await landingPagesRes.value.json();
          newCounts.landingPages = data.landingPages?.length || 0;
        }
        if (supportModulesRes.status === 'fulfilled' && supportModulesRes.value.ok) {
          const data = await supportModulesRes.value.json();
          newCounts.supportModules = data.supportModules?.length || 0;
        }
        if (shortLinksRes.status === 'fulfilled' && shortLinksRes.value.ok) {
          const data = await shortLinksRes.value.json();
          newCounts.shortLinks = data.shortLinks?.length || 0;
        }
        if (documentsRes.status === 'fulfilled' && documentsRes.value.ok) {
          const data = await documentsRes.value.json();
          newCounts.documents = data.documentCount || 0;
        }
        if (internalAdsRes.status === 'fulfilled' && internalAdsRes.value.ok) {
          const data = await internalAdsRes.value.json();
          newCounts.internalAds = data.placements?.length || 0;
        }

        setFeatureCounts(newCounts);
      } catch (error) {
        console.error('Error fetching feature counts for row:', error);
      }
    };

    fetchFeatureCounts();
  }, [project._id, project.metadata?.totalProducts]);

  const renderTickOrDash = (count) => {
    return count > 0 ? (
      <span className="text-green-500 flex justify-center"><FaCheck className="w-3.5 h-3.5" /></span>
    ) : (
      <span className="text-gray-600 flex justify-center">-</span>
    );
  };

  return (
    <tr 
      onClick={() => onView(project)}
      className="hover:bg-gray-900/20 transition-colors cursor-pointer border-b border-gray-800/40 group text-left"
    >
      {/* Project & Company Info */}
      <td className="py-4 px-4 whitespace-nowrap">
        <div className="flex items-center space-x-3">
          <div className="relative flex-shrink-0">
            {project.logo ? (
              <img 
                src={project.logo.startsWith('http') ? project.logo : `${getBaseUrl()}${project.logo}`}
                alt={project.name}
                className="w-10 h-10 rounded-lg object-cover border border-gray-750"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
            ) : null}
            <div 
              className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center border border-gray-800"
              style={{ display: project.logo ? 'none' : 'flex' }}
            >
              <FaFolder className="w-5 h-5 text-gray-400" />
            </div>
            <div className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border border-gray-950 ${getStatusDotColor(project.status)}`} />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-white group-hover:text-indigo-400 transition-colors truncate">
              {project.name}
            </h3>
            <p className="text-xs text-gray-400 truncate">{project.companyName}</p>
          </div>
        </div>
      </td>

      {/* Products Count */}
      <td className="py-4 px-4 text-center whitespace-nowrap font-semibold text-gray-305">
        {featureCounts.products}
      </td>

      {/* Landing Pages */}
      <td className="py-4 px-4 text-center whitespace-nowrap">
        {renderTickOrDash(featureCounts.landingPages)}
      </td>

      {/* Support Modules */}
      <td className="py-4 px-4 text-center whitespace-nowrap">
        {renderTickOrDash(featureCounts.supportModules)}
      </td>

      {/* Short Links */}
      <td className="py-4 px-4 text-center whitespace-nowrap">
        {renderTickOrDash(featureCounts.shortLinks)}
      </td>

      {/* Documents */}
      <td className="py-4 px-4 text-center whitespace-nowrap">
        {renderTickOrDash(featureCounts.documents)}
      </td>

      {/* Internal Ads */}
      <td className="py-4 px-4 text-center whitespace-nowrap">
        {renderTickOrDash(featureCounts.internalAds)}
      </td>

      {/* Actions (Details, Edit) */}
      <td className="py-4 px-4 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end space-x-2">
          <button
            onClick={() => onView(project)}
            className="px-3 py-1.5 bg-gray-850 hover:bg-gray-800 text-xs font-semibold text-white rounded-lg transition-colors border border-gray-800"
          >
            Details
          </button>
          <button
            onClick={() => onEdit(project)}
            className="p-1.5 text-gray-400 hover:text-indigo-400 hover:bg-indigo-500/10 rounded-lg transition-colors"
            title="Edit project"
          >
            <FaEdit className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

const ProjectsSection = ({ user, onLogout, onStatsUpdate }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProjects, setTotalProjects] = useState(0);
  const [limit] = useState(10);
  
  // Toast notification states
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [upgradeModalSection, setUpgradeModalSection] = useState(null);

  // Handle upgrade click
  const handleUpgradeClick = (section = null) => {
    setUpgradeModalSection(section);
    setShowUpgradeModal(true);
  };

  // Check if user can create more projects
  const canCreateProject = () => {
    const userPlan = user?.subscription?.plan || 'free';
    const currentCount = projects.length;
    const userLimit = user?.subscription?.maxProjects;
    
    // Enterprise planında sınırsız
    if (userPlan === 'enterprise' || userLimit === -1) {
      return true;
    }
    
    // Kullanıcının limitini kullan, yoksa fallback
    if (userLimit) {
      return currentCount < userLimit;
    }
    
    // Fallback limitler
    switch (userPlan) {
      case 'free':
        return currentCount < 1;
      case 'premium':
        return currentCount < 5;
      default:
        return currentCount < 1;
    }
  };

  const getProjectLimit = () => {
    const userPlan = user?.subscription?.plan || 'free';
    switch (userPlan) {
      case 'free':
        return 1;
      case 'premium':
        return 5;
      case 'enterprise':
        return 'Unlimited';
      default:
        return 1;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusDotColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-400';
      case 'inactive':
        return 'bg-yellow-400';
      case 'archived':
        return 'bg-gray-400';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-400';
      case 'inactive':
        return 'text-yellow-400';
      case 'archived':
        return 'text-gray-400';
      default:
        return 'text-gray-400';
    }
  };

  const handleUpgradeSuccess = () => {
    setShowUpgradeModal(false);
    showToast('Subscription upgraded successfully! You can now create more projects.', 'success');
    // Refresh projects to update any UI changes
    fetchProjects();
  };

  useEffect(() => {
    fetchProjects();
  }, [currentPage]);

  // Toast notification functions
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  const fetchProjects = async () => {
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }

      setLoading(true);
      const response = await authenticatedFetch(`/api/dashboard/user/projects?page=${currentPage}&limit=${limit}`);
      const data = await response.json();
      setProjects(data.projects || []);
      setTotalPages(data.totalPages || 1);
      setTotalProjects(data.total || 0);
    } catch (error) {
      console.error('Error fetching projects:', error);
      if (error.message === 'Authentication failed') {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (projectData) => {
    setIsSubmitting(true);
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }

      const response = await authenticatedFetch('/api/dashboard/user/projects', {
        method: 'POST',
        body: projectData
      });

      if (response.ok) {
        const result = await response.json();
        setProjects(prev => [...prev, result.project]);
        setShowProjectForm(false);
        setEditingProject(null);
        showToast('Project created successfully!', 'success');
        
        // Update stats in parent component
        if (onStatsUpdate) {
          onStatsUpdate();
        }
      } else {
        const errorData = await response.json();
        // Check if it's a limit error
        if (errorData.message && errorData.message.includes('limitinize ulaştınız')) {
          handleUpgradeClick('projects');
        } else {
          showToast(errorData.message || 'Failed to create project', 'error');
        }
      }
    } catch (error) {
      console.error('Create project error:', error);
      // Check if it's a limit error
      if (error.message && error.message.includes('limitinize ulaştınız')) {
        handleUpgradeClick('projects');
      } else {
        showToast('Failed to create project', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateProject = async (projectId, projectData) => {
    setIsSubmitting(true);
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }

      const response = await authenticatedFetch(`/api/dashboard/user/projects/${projectId}`, {
        method: 'PUT',
        body: projectData
      });

      if (response.ok) {
        const result = await response.json();
        setProjects(prev => prev.map(p => p._id === projectId ? result.project : p));
        setShowProjectForm(false);
        setEditingProject(null);
        showToast('Project updated successfully!', 'success');
      } else {
        const errorData = await response.json();
        showToast(errorData.message || 'Failed to update project', 'error');
      }
    } catch (error) {
      console.error('Update project error:', error);
      showToast('Failed to update project', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProject = async (project) => {
    if (!window.confirm(`Are you sure you want to delete "${project.name}"? This will also delete all related data (products, modules, short links, documents, support tickets). This action cannot be undone.`)) {
      return;
    }

    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }

      const response = await authenticatedFetch(`/api/dashboard/user/projects/${project._id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setProjects(prev => prev.filter(p => p._id !== project._id));
        showToast('Project deleted successfully!', 'success');
        
        // Update stats in parent component
        if (onStatsUpdate) {
          onStatsUpdate();
        }
      } else {
        const errorData = await response.json();
        showToast(errorData.message || 'Failed to delete project', 'error');
      }
    } catch (error) {
      console.error('Delete project error:', error);
      showToast('Failed to delete project', 'error');
    }
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setShowProjectForm(true);
  };

  const handleViewProject = (project) => {
    navigate(`/dashboard/projects/${project._id}`);
  };

  const handleAddProduct = (project) => {
    navigate(`/dashboard/projects/${project._id}?action=add-product`);
  };

  const handleLandingPages = (project) => {
    navigate(`/dashboard/projects/${project._id}/landing-pages`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-black rounded-2xl border border-gray-800 p-6">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-[60] px-6 py-3 rounded-lg shadow-lg transition-all duration-300 ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.message}
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-white">Your Projects</h3>
        <div className="flex items-center space-x-3">
          <Button
            onClick={() => setShowVideoModal(true)}
            className="group flex items-center px-4 py-3 rounded-xl transition-all duration-200 bg-gray-800 text-white hover:bg-gray-700 border border-gray-700 data-hover:bg-gray-700 data-focus:ring-white"
          >
            <FaQuestionCircle className="mr-2 h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
            How to Create?
          </Button>
          <Button
            onClick={() => {
              if (!canCreateProject()) {
                handleUpgradeClick('projects');
                return;
              }
              setEditingProject(null);
              setShowProjectForm(true);
            }}
            className={`group flex items-center px-6 py-3 rounded-xl transition-all duration-200 data-hover:bg-gray-700 data-focus:ring-white border ${
              canCreateProject() 
                ? 'bg-gray-800 text-white hover:bg-gray-700 border-gray-700' 
                : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 border-indigo-500 animate-pulse'
            }`}
          >
            <FaPlus className={`mr-2 h-4 w-4 transition-transform duration-200 ${
              canCreateProject() ? 'group-hover:rotate-90' : 'animate-bounce'
            }`} />
            {canCreateProject() ? 'New Project' : 'Upgrade to Create More'}
          </Button>
        </div>
      </div>

      {projects.length === 0 ? (
        <div className="text-center py-12">
          <div className="mx-auto h-12 w-12 bg-gray-900 rounded-full flex items-center justify-center mb-4">
            <FaFolder className="h-6 w-6 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No projects yet</h3>
          <p className="text-gray-400 mb-6">Create your first project to get started.</p>
          <div className="flex items-center justify-center space-x-3">
            <Button
              onClick={() => setShowVideoModal(true)}
              className="group flex items-center px-4 py-3 rounded-xl transition-all duration-200 bg-gray-800 text-white hover:bg-gray-700 border border-gray-700 data-hover:bg-gray-700 data-focus:ring-white"
            >
              <FaQuestionCircle className="mr-2 h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
              How to Create?
            </Button>
            <Button
              onClick={() => {
                if (!canCreateProject()) {
                  handleUpgradeClick('projects');
                  return;
                }
                setEditingProject(null);
                setShowProjectForm(true);
              }}
              className={`group flex items-center px-6 py-3 rounded-xl transition-all duration-200 data-hover:bg-gray-700 data-focus:ring-white border ${
                canCreateProject() 
                  ? 'bg-gray-800 text-white hover:bg-gray-700 border-gray-700' 
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 border-indigo-500 animate-pulse'
              }`}
            >
              <FaPlus className={`mr-2 h-4 w-4 transition-transform duration-200 ${
                canCreateProject() ? 'group-hover:rotate-90' : 'animate-bounce'
              }`} />
              {canCreateProject() ? 'New Project' : 'Upgrade to Create More'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-800 bg-black">
          <table className="min-w-full divide-y divide-gray-800 text-left text-sm text-gray-300">
            <thead>
              <tr className="text-gray-400 text-xs font-semibold uppercase tracking-wider bg-gray-900/50">
                <th className="py-3.5 px-4">Project / Company</th>
                <th className="py-3.5 px-4 text-center">Products</th>
                <th className="py-3.5 px-4 text-center">Landing</th>
                <th className="py-3.5 px-4 text-center">Support</th>
                <th className="py-3.5 px-4 text-center">Links</th>
                <th className="py-3.5 px-4 text-center">Docs</th>
                <th className="py-3.5 px-4 text-center">Ads</th>
                <th className="py-3.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/40">
              {projects.map((project) => (
                <ProjectRow
                  key={project._id}
                  project={project}
                  onView={handleViewProject}
                  onEdit={handleEditProject}
                  getBaseUrl={getBaseUrl}
                  getStatusDotColor={getStatusDotColor}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-between border-t border-gray-800 pt-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <Button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center rounded-md border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700 disabled:opacity-50"
            >
              Previous
            </Button>
            <Button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700 disabled:opacity-50"
            >
              Next
            </Button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-400">
                Showing <span className="font-medium text-white">{(currentPage - 1) * limit + 1}</span> to{' '}
                <span className="font-medium text-white">
                  {Math.min(currentPage * limit, totalProjects)}
                </span>{' '}
                of <span className="font-medium text-white">{totalProjects}</span> results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <Button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md px-3 py-2 text-gray-400 ring-1 ring-inset ring-gray-700 hover:bg-gray-800 focus:z-20 focus:outline-offset-0 disabled:opacity-30"
                >
                  <span className="sr-only">Previous</span>
                  <FaChevronRight className="h-5 w-5 rotate-180" />
                </Button>
                {[...Array(totalPages)].map((_, i) => (
                  <Button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                      currentPage === i + 1
                        ? 'z-10 bg-indigo-600 text-white focus-visible:outline-indigo-600'
                        : 'text-gray-300 ring-1 ring-inset ring-gray-700 hover:bg-gray-800 focus:outline-offset-0'
                    }`}
                  >
                    {i + 1}
                  </Button>
                ))}
                <Button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-3 py-2 text-gray-400 ring-1 ring-inset ring-gray-700 hover:bg-gray-800 focus:z-20 focus:outline-offset-0 disabled:opacity-30"
                >
                  <span className="sr-only">Next</span>
                  <FaChevronRight className="h-5 w-5" />
                </Button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Project Form Modal */}
      {showProjectForm && (
        <ProjectForm
          isOpen={showProjectForm}
          onClose={() => {
            setShowProjectForm(false);
            setEditingProject(null);
          }}
          onSubmit={editingProject ? (data) => handleUpdateProject(editingProject._id, data) : handleCreateProject}
          project={editingProject}
          isLoading={isSubmitting}
          user={getUser()}
        />
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        user={user}
        onUpgrade={handleUpgradeSuccess}
        highlightSection={upgradeModalSection}
      />

      {/* Video Modal */}
      <VideoModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        videoUrl="https://www.youtube.com/embed/m2iegOmpgck"
        title="How to Create a Project?"
      />
    </div>
  );
};

export default ProjectsSection;
